import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Receipt, Zap } from 'lucide-react';
import TopMenu from '../../components/TopMenu';
import SubscriptionBanner from '../../components/MockInterview/SubscriptionBanner';
import PricingModal from '../../components/MockInterview/PricingModal';
import { getMySubscription } from '../../services/mockInterviewService';
import { hasRole } from '../../utils/auth';
import { useNotification } from '../../notification/NotificationContext';
import '../../components/MockInterview/MockInterview.css';

const MockInterviewDashboard = ({ theme = 'dark', toggleTheme }) => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPricing, setShowPricing] = useState(false);
  const dark = theme === 'dark';

  const loadSub = () =>
    getMySubscription()
      .then(setSub)
      .catch((e) => showNotification(e.message || 'Failed to load subscription', 'error'));

  useEffect(() => {
    loadSub().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exhausted =
    sub?.plan === 'Free' && (sub?.freeMinutesRemaining ?? 0) <= 0;

  if (loading) {
    return (
      <div className={`mi-page ${dark ? '' : 'mi-light'}`}>
        <TopMenu theme={theme} toggleTheme={toggleTheme} />
        <div className="mi-loading"><div className="mi-spin" /></div>
      </div>
    );
  }

  const showBackstage = hasRole('Interviewer') || hasRole('Admin');

  return (
    <div className={`mi-page ${dark ? '' : 'mi-light'}`}>
      <TopMenu theme={theme} toggleTheme={toggleTheme} />

      <div className="mi-ed">
        {/* ── Top strip: issue marker + tools ──────────────────────── */}
        <div className="mi-ed-strip">
          <div className="mi-ed-strip-l">
            <span className="mi-ed-issue">№ 01 — Interview Lab</span>
            <span>Vol. 4 / Spring</span>
            <span className="mi-ed-pulse">Now in session</span>
          </div>
          <div className="mi-row" style={{ gap: 8 }}>
            <button className="mi-btn mi-btn-sm" onClick={() => navigate('/payment/history')}>
              <Receipt size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Billing
            </button>
            <button className="mi-btn mi-btn-sm mi-btn-primary" onClick={() => setShowPricing(true)}>
              <Zap size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Upgrade
            </button>
          </div>
        </div>

        {/* ── Hero ─────────────────────────────────────────────────── */}
        <header className="mi-ed-hero">
          <div>
            <h1 className="mi-ed-display">
              Practice like<br />
              it&rsquo;s the <em>real&nbsp;thing</em>
              <svg
                className="mi-ed-scribble"
                viewBox="0 0 320 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M3 9 C 60 2, 120 12, 180 6 S 280 11, 317 4" />
              </svg>
            </h1>
            <p className="mi-ed-lede">
              Two ways to rehearse before the call that matters. <b>Talk to an AI</b> the
              second nerves hit, or <b>book a real interviewer</b> for the long game.
              Either way you walk out with notes, scores, and a plan.
            </p>
          </div>

          <div className="mi-ed-stamp" aria-hidden="true">
            <span>Reps over</span>
            <b>perfect</b>
            <span>since &rsquo;24</span>
          </div>
        </header>

        {/* ── Editorial meta strip ─────────────────────────────────── */}
        <div className="mi-ed-meta" role="list">
          <div className="mi-ed-meta-item" role="listitem">
            <span className="mi-ed-meta-num">18m</span>
            <span className="mi-ed-meta-lbl">avg session</span>
          </div>
          <div className="mi-ed-meta-item" role="listitem">
            <span className="mi-ed-meta-num">~7s</span>
            <span className="mi-ed-meta-lbl">to first feedback</span>
          </div>
          <div className="mi-ed-meta-item" role="listitem">
            <span className="mi-ed-meta-num">4.8★</span>
            <span className="mi-ed-meta-lbl">candidate rating</span>
          </div>
          <div className="mi-ed-meta-item" role="listitem">
            <span className="mi-ed-meta-num">12k+</span>
            <span className="mi-ed-meta-lbl">reps logged</span>
          </div>
        </div>

        {/* ── Subscription state ──────────────────────────────────── */}
        {sub && (
          <div style={{ marginTop: 24 }}>
            <SubscriptionBanner
              plan={sub.plan}
              freeMinutesUsed={sub.freeMinutesUsed}
              freeMinutesRemaining={sub.freeMinutesRemaining}
            />
          </div>
        )}

        {exhausted && (
          <div className="mi-notice" style={{ marginBottom: 18 }}>
            <h4>Your free minutes are spent.</h4>
            <p>Don&rsquo;t lose momentum — Basic and Pro unlock unlimited sessions.</p>
            <button className="mi-btn mi-btn-primary" onClick={() => setShowPricing(true)}>
              See plans
            </button>
          </div>
        )}

        {/* ── Column header ────────────────────────────────────────── */}
        <div className="mi-ed-colhead">
          <span>The Studio</span>
          <em>pick your weapon</em>
        </div>

        {/* ── Hero card stack: 01 full-width, others in 2-col rows ── */}
        <div className="mi-ed-feature-stack">
          {/* 01 — AI Mock Interview (pink, pulse glow) */}
          <div
            className="mi-ed-card mi-ed-card-hero mi-ed-hero-pink mi-ed-anim-pulse"
            onClick={() => navigate('/ai-interview')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/ai-interview')}
          >
            <div>
              <p className="mi-ed-num">01</p>
              <h3>
                AI Mock<br />
                <em>Interview.</em>
              </h3>
              <p>
                Click. Talk. Done. Our AI runs the round, listens to your
                answers, and hands you a scored breakdown before your coffee
                gets cold.
              </p>
            </div>
            <div className="mi-ed-card-foot">
              <div className="mi-ed-tagrow">
                <span className="mi-ed-pill">Instant</span>
                <span className="mi-ed-pill">Voice + chat</span>
                <span className="mi-ed-pill">Scored</span>
              </div>
              <span className="mi-ed-cta">
                Start a round
                <span className="mi-ed-cta-arrow">
                  <ArrowUpRight size={12} strokeWidth={3} />
                </span>
              </span>
            </div>
          </div>

          {/* 02 + 03 — side by side */}
          <div className="mi-ed-feature-row">
            {/* 02 — Book 1-on-1 (emerald, drifting orbs) */}
            <div
              className="mi-ed-card mi-ed-card-hero mi-ed-hero-emerald mi-ed-anim-drift"
              onClick={() => navigate('/mock-interview/book')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/mock-interview/book')}
            >
              <div>
                <p className="mi-ed-num">02</p>
                <h3>
                  Book a<br />
                  <em>1-on-1.</em>
                </h3>
                <p>
                  Sit down with an actual interviewer. Pick a slot, send
                  the ask, and they&rsquo;ll come back with structured
                  feedback.
                </p>
              </div>
              <div className="mi-ed-card-foot">
                <div className="mi-ed-tagrow">
                  <span className="mi-ed-pill">Real human</span>
                  <span className="mi-ed-pill">Scheduled</span>
                </div>
                <span className="mi-ed-cta">
                  Browse slots
                  <span className="mi-ed-cta-arrow">
                    <ArrowUpRight size={12} strokeWidth={3} />
                  </span>
                </span>
              </div>
            </div>

            {/* 03 — My Feedback (indigo, shimmer sweep) */}
            <div
              className="mi-ed-card mi-ed-card-hero mi-ed-hero-indigo mi-ed-anim-sweep"
              onClick={() => navigate('/mock-interview/my-feedback')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/mock-interview/my-feedback')}
            >
              <div>
                <p className="mi-ed-num">03</p>
                <h3>
                  My<br />
                  <em>feedback.</em>
                </h3>
                <p>
                  Skill-by-skill scores and written notes from every 1-on-1
                  you&rsquo;ve completed. Spot the patterns that keep
                  showing up.
                </p>
              </div>
              <div className="mi-ed-card-foot">
                <div className="mi-ed-tagrow">
                  <span className="mi-ed-pill">Archive</span>
                  <span className="mi-ed-pill">Per-skill</span>
                </div>
                <span className="mi-ed-cta">
                  Open archive
                  <span className="mi-ed-cta-arrow">
                    <ArrowUpRight size={12} strokeWidth={3} />
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <p className="mi-ed-margin" style={{ marginTop: 22 }}>
          New here? Start with <b>01</b>. The AI version is the warm-up;
          humans hit harder.
        </p>

        {/* ── Backstage (admin/interviewer) — same hero pattern ───── */}
        {showBackstage && (
          <section style={{ marginTop: 50 }}>
            <div className="mi-ed-colhead" style={{ margin: '0 0 16px' }}>
              <span>Backstage</span>
              <em>staff only</em>
            </div>
            <div className="mi-ed-feature-row">
              {hasRole('Interviewer') && (
                <div
                  className="mi-ed-card mi-ed-card-hero mi-ed-hero-amber mi-ed-anim-orbit"
                  onClick={() => navigate('/mock-interview/interviewer')}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/mock-interview/interviewer')}
                >
                  <div>
                    <p className="mi-ed-num">04</p>
                    <h3>
                      Interviewer<br />
                      <em>dashboard.</em>
                    </h3>
                    <p>
                      Triage incoming requests, reschedule slots, and
                      submit feedback for the candidates you&rsquo;ve sat
                      with.
                    </p>
                  </div>
                  <div className="mi-ed-card-foot">
                    <div className="mi-ed-tagrow">
                      <span className="mi-ed-pill">Triage</span>
                      <span className="mi-ed-pill">Schedule</span>
                    </div>
                    <span className="mi-ed-cta">
                      Open dashboard
                      <span className="mi-ed-cta-arrow">
                        <ArrowUpRight size={12} strokeWidth={3} />
                      </span>
                    </span>
                  </div>
                </div>
              )}

              {hasRole('Admin') && (
                <div
                  className="mi-ed-card mi-ed-card-hero mi-ed-hero-purple mi-ed-anim-grid"
                  onClick={() => navigate('/mock-interview/admin')}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && navigate('/mock-interview/admin')}
                >
                  <div>
                    <p className="mi-ed-num">{hasRole('Interviewer') ? '05' : '04'}</p>
                    <h3>
                      Manage<br />
                      <em>interviewers.</em>
                    </h3>
                    <p>
                      Onboard staff, toggle who&rsquo;s taking calls this
                      week, and publish open slots so candidates can book.
                    </p>
                  </div>
                  <div className="mi-ed-card-foot">
                    <div className="mi-ed-tagrow">
                      <span className="mi-ed-pill">Roster</span>
                      <span className="mi-ed-pill">Slots</span>
                    </div>
                    <span className="mi-ed-cta">
                      Curate roster
                      <span className="mi-ed-cta-arrow">
                        <ArrowUpRight size={12} strokeWidth={3} />
                      </span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}
      </div>

      <PricingModal
        open={showPricing}
        onClose={() => setShowPricing(false)}
        onSubscribed={loadSub}
      />
    </div>
  );
};

export default MockInterviewDashboard;
